import { Box, Typography } from '@mui/material';
import { Identifier, MatchPlayerStatus } from '@okletsplay/core';
import { EntityStoreName } from 'app/enums/store';
import { dispatchUnwrap } from 'app/utils/store';
import classNames from 'classnames';
import { CurrentMatchStoreStatus } from 'modules/matches/store/current-match/enums';
import {
  forfeitMatchRequest,
  getMatchGameUrl,
} from 'modules/matches/store/current-match/repository';
import React, { FC, useCallback, useEffect, useMemo, useState } from 'react';
import { Loader } from 'shared/components/Loader';
import { useEntityRepositoryStatus } from 'app/hooks/store';
import { useStyles } from './MatchLeaderboardModalActions.styles';
import { LoaderButton } from 'shared/components/LoaderButton/LoaderButton';

interface MatchLeaderboardModalActionsProps {
  matchId: Identifier;
  playerStatus: MatchPlayerStatus;
  hideForfeit?: boolean;
}

export const MatchLeaderboardModalActions: FC<MatchLeaderboardModalActionsProps> = ({
  matchId,
  playerStatus,
  hideForfeit = false,
}) => {
  const { classes } = useStyles();
  const [matchUrl, setMatchUrl] = useState<string>();
  const [isMatchUrlLoading, setMatchUrlLoading] = useState<boolean>(false);

  const isCurrentUserPlayed = useMemo(
    () => playerStatus === MatchPlayerStatus.Played,
    [playerStatus]
  );

  const isCurrentUserReady = useMemo(
    () => playerStatus === MatchPlayerStatus.Ready,
    [playerStatus]
  );

  const forfeiting = useEntityRepositoryStatus({
    [EntityStoreName.CurrentMatch]: CurrentMatchStoreStatus.Forfeiting,
  });

  const loading = useEntityRepositoryStatus({
    [EntityStoreName.CurrentMatch]: CurrentMatchStoreStatus.Loading,
  });

  const playGameHandler = useCallback(() => {
    window.open(matchUrl, '_blank');
  }, [matchUrl]);

  const forfeitMatchHandler = useCallback(() => {
    if (matchId) {
      dispatchUnwrap(forfeitMatchRequest(matchId));
    }
  }, [matchId]);

  useEffect(() => {
    if (!isCurrentUserReady) return;

    setMatchUrlLoading(true);

    dispatchUnwrap<string>(getMatchGameUrl(matchId), {
      success: (token) => {
        setMatchUrl(token);
        setMatchUrlLoading(false);
      },
      error: () => {
        setMatchUrlLoading(false);
      },
    });
  }, [isCurrentUserReady, matchId]);

  if (isCurrentUserPlayed) {
    return (
      <Typography className={classes.text} variant="body1">
        You submitted your score! Sit tight and wait for the others.
      </Typography>
    );
  }

  if (loading) {
    return <Loader size={24} />;
  }

  return (
    <Box className={classes.buttonContainer}>
      {playerStatus === MatchPlayerStatus.Requested ? (
        <Typography textAlign="center" mb={1}>
          Waiting for the host to accept your join request.
        </Typography>
      ) : null}

      {hideForfeit ? null : (
        <LoaderButton
          size="medium"
          color="error"
          variant="outlined"
          loading={forfeiting}
          onClick={forfeitMatchHandler}
          className={classNames(classes.button, classes.forfeit)}
          disabled={!isCurrentUserReady}
        >
          Forfeit
        </LoaderButton>
      )}

      <LoaderButton
        size="medium"
        color="primary"
        variant="contained"
        loading={isMatchUrlLoading}
        onClick={playGameHandler}
        className={classes.button}
        disabled={!matchUrl}
      >
        Play Game!
      </LoaderButton>
    </Box>
  );
};
